import { Box, Collapse, Group, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface SettingsGroupProps {
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  count: number;
  children: ReactNode;
  defaultOpened?: boolean;
  /** 外部触发展开的信号：值每次变化（且非初始值）时强制展开该分组，用于锚点导航点击自动打开折叠的卡片 */
  openSignal?: number;
}

export const SettingsGroup = ({
  icon,
  iconBg,
  iconColor,
  title,
  count,
  children,
  defaultOpened = true,
  openSignal,
}: SettingsGroupProps) => {
  const { t } = useTranslation();
  const [opened, { toggle, open }] = useDisclosure(defaultOpened);

  // 点击右侧锚点导航时，如果对应卡片处于折叠状态则自动展开
  const openSignalRef = useRef(openSignal);
  useEffect(() => {
    if (openSignal !== openSignalRef.current) {
      openSignalRef.current = openSignal;
      open();
    }
  }, [openSignal, open]);

  return (
    <Box
      className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md"
      mb={16}
    >
      <UnstyledButton
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <Group gap={10}>
          <Box
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: iconBg, color: iconColor }}
          >
            {icon}
          </Box>
          <Text fw={600} size="md">
            {title}
          </Text>
          <Text size="xs" c="dimmed" className="bg-gray-100 px-2 py-0.5 rounded-xl">
            {t('settings.group.item_count', { count })}
          </Text>
        </Group>
        <IconChevronDown
          size={20}
          color="var(--mantine-color-gray-5)"
          style={{
            transform: opened ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </UnstyledButton>
      <Collapse in={opened}>
        <Box px={20} pb={20}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};
